import React, { useEffect } from "react";
import { EntityId } from "@reduxjs/toolkit";
import { OnChangeParams, Value } from "baseui/select";
import { Issue, IssueType } from "@store/issue/types";
import { selectSpaceById } from "@store/space/spaceSelectors";
import useAppSelector from "@hooks/useAppSelector";
import { selectUserById } from "@store/user/userSelectors";
import { getFormattedActiveIssues } from "@store/issue/issueHelpers";
import AttachmentDrop from "./attachments/AttachmentDrop";
import NewIssueForm from "./NewIssueForm";

interface NewIssueScreenProps {
  sections: { title: string; data: { value: EntityId; label: string }[] }[];
  issue: Omit<Issue, "id">;
  setIssue: React.Dispatch<React.SetStateAction<Omit<Issue, "id">>>;
  onSubmit: () => void;
  canSubmit: boolean;
  isInEditMode: boolean;
}

const NewIssueScreen: React.FC<NewIssueScreenProps> = ({
  sections,
  issue,
  setIssue,
  onSubmit,
  canSubmit,
  isInEditMode,
}) => {
  const { spaceId, authorId } = issue;

  const [activeIssues, setActiveIssues] = React.useState<string[][]>([]);

  const spaceLabel = useAppSelector((state) =>
    selectSpaceById(state.space, spaceId),
  );

  const user = useAppSelector(({ user }) =>
    selectUserById(user, authorId as EntityId),
  );

  const onTogglePriority = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIssue({ ...issue, priority: event.target.checked });
  };

  const options: Value | undefined = sections.flatMap(
    (section) => section.data,
  );

  const onSelectLocation = ({ value }: OnChangeParams) => {
    if (value[0]) setIssue({ ...issue, spaceId: value[0].value });
    else setIssue({ ...issue, spaceId: "" });
  };

  const handleIssueChange = (
    baseIssue: Pick<Issue, "label" | "description" | "reportedBy">,
  ) => {
    setIssue({
      ...issue,
      ...baseIssue,
    });
  };

  const handleTypeChange = (issueType: IssueType) => {
    setIssue({
      ...issue,
      typeOf: issueType,
    });
  };

  useEffect(() => {
    if (isInEditMode || !spaceId) return;

    const fetchActiveIsues = async () => {
      const issues = await getFormattedActiveIssues(spaceId, issue.typeOf);
      setActiveIssues(issues);
    };

    fetchActiveIsues();
  }, [spaceId, isInEditMode, issue.typeOf]);

  return (
    <NewIssueForm
      onSelectLocation={onSelectLocation}
      locations={options}
      selectedLocations={
        spaceLabel ? [{ id: spaceLabel.id, label: spaceLabel.label }] : []
      }
      isInEditMode={isInEditMode}
      activeIssues={activeIssues}
      baseIssue={issue}
      onBaseIssueChange={handleIssueChange}
      priority={issue.priority}
      onTogglePriority={onTogglePriority}
      onSubmit={onSubmit}
      user={user}
      canSubmit={canSubmit}
      issueType={issue.typeOf}
      changeType={handleTypeChange}
      isRecurringIssue={false}>
      <AttachmentDrop
        attachments={issue.attachments}
        onChange={(attachments) =>
          setIssue((prev) => ({ ...prev, attachments }))
        }
      />
    </NewIssueForm>
  );
};
export default NewIssueScreen;
